import { useSyncExternalStore } from "react";

export type SaveStatus = "idle" | "saving" | "failed";

let activeSaves = 0;
let lastSaveFailed = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

/**
 * Starts tracking one in-flight save; returns a release function. Pass
 * `true` (or omit) on success — this also clears any prior failure — or
 * `false` on failure, which marks the status "failed" until the next
 * successful save completes.
 */
export function trackSave(): (success?: boolean) => void {
  let released = false;
  activeSaves += 1;
  emit();

  return (success = true) => {
    if (released) return;
    released = true;
    activeSaves = Math.max(0, activeSaves - 1);
    lastSaveFailed = !success;
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SaveStatus {
  if (activeSaves > 0) return "saving";
  if (lastSaveFailed) return "failed";
  return "idle";
}

export function useSaveStatus(): SaveStatus {
  return useSyncExternalStore(subscribe, getSnapshot, () => "idle");
}

/** True while any save is in flight. Kept for existing boolean consumers. */
export function useSavingStatus(): boolean {
  return useSaveStatus() === "saving";
}

/** Plain-function snapshot for non-React callers (the beforeunload guard in
 * AppShell) that need to know synchronously whether a save is in flight. */
export function isSaveInFlight(): boolean {
  return activeSaves > 0;
}
